import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { theseus2 } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { selectUser } from '../../../../modules/user/selectors'
import TaskBody from '../../TaskBody'
import { SubTask } from '../../types'
import ThemedButton from '../../../ThemedButton'
import { Box } from '../../../UI/Box'
import useIsLoggedIn from '../../../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../../Library/Buttons/ConnectWalletButton'
import TelegramConnectButton from '../../../SocialConnectButtons/Telegram'

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const TelegramTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Join our Telegram community'
  const description =
    instructions ??
    'Simply press the join button below to get our updates and then verify your participation'

  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)

  const { isLoggedIn } = useIsLoggedIn()

  const location = useLocation()
  const history = useHistory()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const updateSearchParam = useCallback(
    (paramName: string, value: string) => {
      searchParams.set(paramName, value)
      history.push(`${location.pathname}?${searchParams.toString()}`)
    },
    [history, location.pathname, searchParams],
  )

  const closeModal = useCallback(() => {
    updateSearchParam('active_mission', '')
  }, [updateSearchParam])

  function selfDestruct(ms = 2000) {
    setTimeout(closeModal, ms)
  }

  async function handleVerification() {
    try {
      setError(null)
      setIsVerifying(true)
      const { data } = await theseus2.post(`/auth/telegram/verify`, {
        subtaskId: subtask.id,
        projectId: project.id,
        taskId,
      })
      if (!data) throw new Error('Failed to follow')
      const { message } = data
      if (message !== 'success') throw new Error('Failed to follow')
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error)
      setError(error?.data?.message ?? 'Failed to verify. Please try again')
    } finally {
      setIsVerifying(false)
    }
  }

  const fetchTelegramInfo = useCallback(async () => {}, [])

  useEffect(() => {
    if (!isLoggedIn) return
    fetchTelegramInfo()
  }, [fetchTelegramInfo, isLoggedIn])

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={error}
      extra={taskAmount}
      actions={
        <ActionBlock
          channelName={subtask.telegramUrl}
          onVerify={handleVerification}
          isLoading={isVerifying}
          isVerified={isComplete}
        />
      }
    />
  )
}

export default TelegramTask

function ActionBlock({
  channelName,
  onVerify,
  isLoading,
  isVerified,
}: {
  channelName: string
  isLoading: boolean
  isVerified: boolean
  onVerify: () => void
}) {
  const { isLoggedIn } = useIsLoggedIn()
  const user = useSelector(selectUser)
  const { telegramHandle } = user ?? {}

  return (
    <Box jsa>
      <ThemedButton
        href={`https://t.me/${channelName}`}
        text="Open channel to join"
        isExternal
      />
      {!isLoggedIn ? (
        <ConnectWalletButton label="Connect to Verify" />
      ) : !telegramHandle ? (
        <TelegramConnectButton label="Connect Telegram" />
      ) : (
        <ThemedButton
          $styleType="primary"
          text={isVerified ? 'Verified' : 'Verify'}
          onClick={onVerify}
          disabled={isLoading || isVerified}
        />
      )}
    </Box>
  )
}
